// RefundFullfillmentStore
import {defineStore} from 'pinia'
import {ref} from 'vue';
import * as _ from 'lodash';   
import {IUpdatedClaimEntityRefunds,IRefundHeaders,IRefundReturnRequest,IRefundReturns,IClaimProcedure, ITransferReturns,ITransferReturnRequest} from '../../lib/Models/ClaimModel';
import {ErrorReturnMessage,getCompanySetting,refundReturn,transferReturn} from '../../../src/lib/Api/Claim';  
import {popAlert,ModalStyles} from '../../lib/Notification'; 

enum refundPayerType {
    patient = "patient",
    payer = "payer",
    responsible_party = "responsible_party"
};
enum transferAmountType {
    updatedBalance,
    availableBalance,
    updatedCredit,
    availableCredit,
    allocated
}
interface IRefundPatientCreditBalance {
    patientID?: number,
    patientName?: string,
    patientTooltip?: string,
    patientBalance?: number,
};
interface IRefundPaymentType {
    patientType: string, 
    patientMethod: string, 
    insuranceType: string, 
    insuranceMethod: string,
    transferType: string,
    transferMethod: string
};
interface ITransferData {
    AutoDistribute: boolean,
    ClaimTotal: number, 
    ClaimRefreshed: number, 
    ProcedureTotal: number, 
    ProcedureRefreshed: number, 
    AvailableCredit: number, 
    UpdatedCredit: number, 
    AvailableBalance: number, 
    Allocated: number, 
    AllocateError: boolean,
    TransferError: boolean,
    AllocateErrorLessThan: boolean
};
export const useRefundFullfillmentStore = defineStore('RefundFullfillmentStore', () => {
    const transferTotals = ref<ITransferData>({AutoDistribute: false,ClaimTotal: 0, ClaimRefreshed: 0, ProcedureTotal: 0, ProcedureRefreshed: 0, AvailableCredit: 0, UpdatedCredit: 0, AvailableBalance: 0, Allocated: 0, AllocateError: false, TransferError: false, AllocateErrorLessThan: false});  
    const procedureTransfers = ref<IClaimProcedure[]>([]);
    const refundPatientCreditBalance = ref<IRefundPatientCreditBalance>({patientID: 0, patientName: "", patientTooltip: "", patientBalance: 0}); 
    const updated_refund_amounts = ref<IUpdatedClaimEntityRefunds[]>([]);
    const save_refunds = ref<IUpdatedClaimEntityRefunds[]>([]); 
    const refund_headers = ref<IRefundHeaders[]>([]);
    const refund_save_disabled = ref<boolean>(true);
    const transfer_save_disabled = ref<boolean>(true);
    const refund_error = ref<boolean>(false);
    const refund_settings = ref<IRefundPaymentType>(
        {patientType : "", patientMethod: "", insuranceType: "", insuranceMethod: "", transferType: "", transferMethod: ""});

    function clearRefunds(): void {  
        updated_refund_amounts.value = [];
        refund_headers.value = [];
        save_refunds.value = [];
        refund_save_disabled.value = true;
    } 
    function clearProcedureTransfers(newClaimTotal : number, autodistribute: boolean) : void {
        procedureTransfers.value = [];        
        transferTotals.value = {AutoDistribute: autodistribute, ClaimTotal: newClaimTotal, ProcedureTotal: 0, ClaimRefreshed: 0, ProcedureRefreshed: 0, AvailableCredit: 0, UpdatedCredit: 0, AvailableBalance: 0, Allocated: 0, AllocateError: false, TransferError: false, AllocateErrorLessThan: false};
        setTransferCredits();
    } 
    function setProcedureTransferTotals(newProcedureTotal : number) : void {
        transferTotals.value.ClaimRefreshed++;
        transferTotals.value.ProcedureTotal += newProcedureTotal;
    }
    function loadProcedureTransfers(procedureDetails : IClaimProcedure, updateList: boolean) : void {
        if (updateList) {
            const search_index = procedureTransfers.value.findIndex(item => item.diagnosisID == procedureDetails.diagnosisID);
            if (search_index > -1) {
                procedureTransfers.value[search_index] = procedureDetails;
            }
            else {
                procedureDetails.transferAmount = 0;
                procedureTransfers.value.push(procedureDetails);
            }
        }
        transferTotals.value.ProcedureRefreshed++;
        if ((transferTotals.value.ClaimTotal > 0) && (transferTotals.value.ClaimTotal === transferTotals.value.ClaimRefreshed) && (transferTotals.value.ProcedureTotal === transferTotals.value.ProcedureRefreshed)) {
            setTransferAvailableBalance();
        }
    }
    function setTransferAvailableBalance() : void { 
        transferTotals.value.AvailableBalance = procedureTransfers.value.map(item => (item.balance ?? 0)).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        transferAutoDistribute();
    }
    function transferAutoDistribute() : void {
        let available_credit = transferTotals.value.UpdatedCredit;
        const sorted_procedures = _.sortBy(procedureTransfers.value.map(item => ({claimID: item.claimID, diagnosisLine: item.diagnosisLine, diagnosisID: item.diagnosisID, fromDate: new Date(item.fromDate)})), ["fromDate","claimID", "diagnosisLine", "diagnosisID"]);

        sorted_procedures.forEach((procedureLine) => {
            let itemID = procedureTransfers.value.findIndex(item => item.diagnosisID === procedureLine.diagnosisID);
            if (!transferTotals.value.AutoDistribute) {
                procedureTransfers.value[itemID].transferAmount = 0;
            }
            else if ((procedureTransfers.value[itemID].balance ?? 0) <= available_credit) {
                procedureTransfers.value[itemID].transferAmount = procedureTransfers.value[itemID].balance ?? 0;
                available_credit -= procedureTransfers.value[itemID].balance ?? 0;
            }
            else if (available_credit > 0) {
                procedureTransfers.value[itemID].transferAmount = available_credit;
                available_credit = 0;
            }
            else {
                procedureTransfers.value[itemID].transferAmount = 0;
            }
        });
        setTransferUpdatedBalances();
    }
    function setTransferUpdatedBalances() : void {
        transferTotals.value.Allocated = procedureTransfers.value.map(item => item.transferAmount ?? 0).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        transferTotals.value.TransferError = (procedureTransfers.value.filter(item => (item.transferAmount ?? 0) > (item.balance ?? 0)).length > 0);
        CompareBalanceToCredit();
    }

    function CompareBalanceToCredit() : void { 
        //adding .toFixed to prevent floating point errors/precision errors 
        transferTotals.value.AllocateError = (transferTotals.value.UpdatedCredit.toFixed(2) < transferTotals.value.Allocated.toFixed(2));
        transferTotals.value.AllocateErrorLessThan = (transferTotals.value.UpdatedCredit.toFixed(2) > transferTotals.value.Allocated.toFixed(2));
        transfer_save_disabled.value = (transferTotals.value.TransferError || transferTotals.value.AllocateError || transferTotals.value.AllocateErrorLessThan || refund_error.value || transferTotals.value.Allocated === 0);
    }
    function GetTransferAmounts(amountType : transferAmountType) : number {
        switch (amountType) {
            case transferAmountType.updatedBalance:
                return transferTotals.value.AvailableBalance - transferTotals.value.Allocated;
            case transferAmountType.availableBalance:
                return transferTotals.value.AvailableBalance;
            case transferAmountType.availableCredit:
                return transferTotals.value.AvailableCredit;
            case transferAmountType.updatedCredit:
                return transferTotals.value.UpdatedCredit;
            case transferAmountType.allocated:
                return transferTotals.value.Allocated;
            default:
                return 0;
        }
    }
    function updateRefunds(claimRefunds : IUpdatedClaimEntityRefunds[]) : void { 
        if (!updated_refund_amounts.value) {
            updated_refund_amounts.value = claimRefunds;
            buildHeaderRefunds();
            return;
        }
        claimRefunds.forEach((refundItem) => {
            const search_index = updated_refund_amounts.value.findIndex(item => item.claimID === refundItem.claimID && item.refundEID === refundItem.refundEID && item.refundArea === refundItem.refundArea);
            if (search_index > -1) {
                updated_refund_amounts.value[search_index].newRefundTotal = refundItem.newRefundTotal;
            }
            else {
                updated_refund_amounts.value.push(refundItem);
            }
            buildHeaderRefunds();
        })
    }
    function buildHeaderRefunds() : void { 
        const key = 'refundEID';
        const refund_entity_totals = 
            [...new Map(updated_refund_amounts.value
                .map(item => ({refundEID: item.refundEID, payerType: item.payerType, payerName: item.payerName, maxRefund: getEntityTotalRefund("refundTotal",item.refundEID), selectedRefund: getEntityTotalRefund("newRefundTotal",item.refundEID), paymentType: getPaymentTypeSetting(item.payerType)}))
                .map(item =>[item[key], item])).values()
            ];
        refund_headers.value = refund_entity_totals.filter(item => item.maxRefund > 0);
        buildSaveRefunds();
    }
    function getEntityTotalRefund(fieldName: string, refundEID : number) : number {
        return updated_refund_amounts.value.filter(item => item.refundEID === refundEID).map(item => (fieldName === "refundTotal" ? item.refundTotal : item.newRefundTotal)).flat().reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    }
    function buildSaveRefunds() : void {
        save_refunds.value = updated_refund_amounts.value.filter(item => item.newRefundTotal > 0 && item.newRefundTotal <= item.refundTotal);
        refund_error.value  = (updated_refund_amounts.value.filter(item => item.newRefundTotal > item.refundTotal).length ? true : false);
        refund_save_disabled.value = (refund_error.value || save_refunds.value.length === 0 ? true : false);
        setTransferCredits();
    }
    function setTransferCredits() : void {
        transferTotals.value.AvailableCredit = updated_refund_amounts.value.map(item => (item.payerType == refundPayerType.payer ? 0 : item.refundTotal)).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        transferTotals.value.UpdatedCredit = updated_refund_amounts.value.map(item => item.newRefundTotal).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        CompareBalanceToCredit();
    }
    async function saveTransfers(paymentMethodID: string,paymentMethodTypeID : string, notes? : string) : Promise<boolean> {
        const refundList : IRefundReturns[] = save_refunds.value.map(item => ({claimID: item.claimID, refundEID: item.refundEID, refundArea: item.refundArea, refundAmount: item.newRefundTotal, paymentType: +paymentMethodTypeID}))
        const transferList : ITransferReturns[] = procedureTransfers.value.filter(item => (item.transferAmount ?? 0) > 0).map(item =>({claimID: item.claimID, diagnosisID: item.diagnosisID, allocatedAmount: (item.transferAmount ?? 0)}));
        const transferReturnRequest : ITransferReturnRequest = {paymentMethodID: paymentMethodID, paymentMethodTypeID: paymentMethodTypeID, notes: notes, refundReturnList: refundList, transferReturnList: transferList};        
        const returnResults = await transferReturn(transferReturnRequest);
        const messageTitle = "Build Transfer Returns";
        if (!returnResults.success) {  
            popAlert(returnResults.error?.error ?? ErrorReturnMessage.transferReturn,messageTitle,ModalStyles.danger);
            return false;
        } 
        if (returnResults.success.errorCode !== 0) { 
            popAlert(returnResults.success.errorMessage,messageTitle,ModalStyles.danger);
            return false; 
        }        
        return true;
    }
    async function saveRefunds(notes? : string) : Promise<boolean> {
        const refundReturns : IRefundReturns[] = save_refunds.value.map(item => ({claimID: item.claimID, refundEID: item.refundEID, refundArea: item.refundArea, refundAmount: item.newRefundTotal, paymentType: getEntityPaymentType(item.refundEID)}))
        const refundReturnRequest : IRefundReturnRequest = {notes: notes, refundReturnList: refundReturns};        
        const returnResults = await refundReturn(refundReturnRequest);
        const messageTitle = "Build Refund Returns";
        if (!returnResults.success) {  
            popAlert(returnResults.error?.error ?? ErrorReturnMessage.refundReturn,messageTitle,ModalStyles.danger);
            return false;
        } 
        if (returnResults.success.errorCode !== 0) { 
            popAlert(returnResults.success.errorMessage,messageTitle,ModalStyles.danger);
            return false; 
        }
        return true;
    }
    function getEntityPaymentType(refundEID : number) : number {
        const refundIndex = refund_headers.value.findIndex(item => item.refundEID === refundEID);
        return +refund_headers.value[refundIndex].paymentType;

    }
    async function getRefundSettings(): Promise<void> { 
        refund_settings.value.patientType  = await getCompanySetting("CREDT",1);
        refund_settings.value.patientMethod = await getCompanySetting("CREDT",2);
        refund_settings.value.insuranceType = await getCompanySetting("CREDT",3); 
        refund_settings.value.insuranceMethod = await getCompanySetting("CREDT",4);
    }
    async function getTransferSettings(): Promise<void> {
        refund_settings.value.transferType = await getCompanySetting("CREDT",5);
        refund_settings.value.transferMethod = await getCompanySetting("CREDT",6);
    }
    function getPaymentTypeSetting(payer_type : string) : string { 
        if (payer_type === refundPayerType.payer) {
            return refund_settings.value.insuranceType;
        } 
        if ((payer_type === refundPayerType.patient) || (payer_type === refundPayerType.responsible_party)) {
            return refund_settings.value.patientType;
        }
        return "";
    } 
    function getTransferType() : string { 
        return refund_settings.value.transferType
    } 
    function getTransferMethod() : string { 
        return refund_settings.value.transferMethod
    }

    return {
        refundPatientCreditBalance, updated_refund_amounts, save_refunds, refund_headers, refund_save_disabled, refund_error, refundPayerType,
        procedureTransfers, transferAmountType, transferTotals, transfer_save_disabled,
        clearProcedureTransfers, loadProcedureTransfers, setProcedureTransferTotals, setTransferUpdatedBalances, GetTransferAmounts,
        getTransferType, getTransferMethod, transferAutoDistribute, saveTransfers,
        getRefundSettings, getTransferSettings, clearRefunds, updateRefunds, saveRefunds};
});


